import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageSearch, Search } from 'lucide-react';
import { Button } from '@siliconx/shared/ui/Button';
import { TextInput } from '@siliconx/shared/ui/Field';
import type { MarketplaceListResponse } from '@siliconx/shared/dto/marketplace';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { priceText } from '@siliconx/shared/ui/buyer-listing/formatPriceRange';

/**
 * SIL-1074/SIL-1103 — "Add from marketplace" control for the purchase-analysis
 * builder (design spec §4 "Rows are added from a SKU picker ... or from a
 * lot, which resolves to SKU lines and keeps a 'from lot X' breadcrumb").
 *
 * Picking a lot or listing does not create a lot-typed line — the lot's
 * composition can change later, so `POST .../lines/from-listing` resolves
 * it to ordinary SKU lines AT PICK TIME and stamps `sourceListingId` on each
 * as provenance only (spec §1 "there is deliberately no line_type = 'lot'").
 * The imported lines are then fully independent and editable, same as any
 * other line.
 *
 * Kept as its own component/file (not inlined in $analysisId.tsx) to
 * minimize merge surface with the parallel SIL-1073 branch, which also
 * touches the builder page.
 *
 * SIL-1103: replaces the v1 raw-id `window.prompt` with a real browse-and-pick
 * modal over the existing buyer-safe marketplace browse endpoint
 * (`GET /api/client/marketplace`, RUF-310) — the same buyer-safe projection
 * the /marketplace page itself reads. No new endpoint, no seller identity or
 * FMV data in the payload; only `id` + the buyer-safe display snapshot is
 * ever rendered here.
 */
export function AddFromListingButton({
  analysisId,
  onImported,
}: {
  analysisId: string;
  onImported: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
        <PackageSearch className="mr-1 size-4" /> Add from marketplace
      </Button>
      {open && (
        <ListingPickerModal
          analysisId={analysisId}
          onClose={() => setOpen(false)}
          onImported={() => {
            setOpen(false);
            onImported();
          }}
        />
      )}
    </>
  );
}

function ListingPickerModal({
  analysisId,
  onClose,
  onImported,
}: {
  analysisId: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const [term, setTerm] = useState('');
  const [importingId, setImportingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // No text-search param on GET /api/client/marketplace — filter client-side
  // over the buyer-safe displayTitle, which the marketplace browse page also
  // relies on (all buyer-visible listings, not just this org's).
  const listingsQ = useQuery<MarketplaceListResponse>({
    queryKey: qk.marketplace({}),
    queryFn: async () => {
      const res = await api.api.client.marketplace.$get({ query: {} });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as MarketplaceListResponse;
    },
  });

  const rows = useMemo(() => {
    const all = listingsQ.data?.data ?? [];
    const needle = term.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((l) => (l.displayTitle ?? '').toLowerCase().includes(needle));
  }, [listingsQ.data, term]);

  async function handleImport(listingId: string) {
    setImportingId(listingId);
    setError(null);
    try {
      const res = await api.api.client['purchase-analyses'][':id'].lines['from-listing'].$post({
        param: { id: analysisId },
        json: { listingId },
      });
      await throwIfNotOk(res);
      onImported();
    } catch (ex) {
      setError((ex as Error).message ?? 'Could not import this listing.');
    } finally {
      setImportingId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-base font-semibold text-zinc-900">Add lines from the marketplace</h2>
        <p className="mb-4 text-sm text-zinc-500">
          Pick an active listing or lot to import as SKU lines, tagged with a "from lot" breadcrumb.
        </p>

        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
          <TextInput
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Filter by title…"
            className="pl-8"
          />
        </div>

        {error && <p className="mb-2 text-xs text-red-600">{error}</p>}

        <div className="max-h-80 overflow-y-auto rounded-md border border-zinc-200">
          {listingsQ.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-500">
              <Loader2 className="size-4 animate-spin" /> Loading listings…
            </div>
          ) : rows.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-zinc-500">
              {term ? 'No matching listings.' : 'No active listings to import from.'}
            </p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {rows.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-zinc-900">
                      {l.displayTitle ?? 'Untitled listing'}
                    </p>
                    <p className="truncate text-xs text-zinc-500">
                      {l.listingType === 'lot' ? 'Lot' : l.listingType === 'sku' ? 'SKU listing' : 'Asset listing'}
                      {' · '}
                      {priceText(l.pricing)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void handleImport(l.id)}
                    disabled={importingId !== null}
                  >
                    {importingId === l.id ? 'Importing…' : 'Add'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
